/**
 * kesslers-datalayer.js
 * Storefront data layer pushes for DV360 / Evolear (GTM-5NFR2R3).
 *
 *  - kesslers_add_to_cart (GA4 add_to_cart payload): pushed whenever anything on the storefront adds to the cart
 *    (main form, sticky ATC bar, quick-buy modals, Swym, etc.). Works by watching
 *    responses from /cart/add and /cart/add.js, so it only fires on a successful add.
 *  - submit_appointment: pushed after a successful Hulk "Request Appointment" form
 *    submission (product page #make-appointment modal and /pages/happiness-by-appointment).
 *
 * Loaded ONCE from the footer Custom Liquid section:
 *   <script src="{{ 'kesslers-datalayer.js' | asset_url }}" defer></script>
 *
 * Sept 28, 2026: added a load guard + short-window dedupe (Evolear saw add_to_cart
 * twice per add) and the submit_appointment event.
 *
 * Config: window.kesslersDL.country — Merchant Center feed label, 'ZZ' (confirmed by Evolear Sept 15, 2026).
 * Debug:  add ?kdl_debug=1 to any URL to log pushes and Hulk messages to the console.
 */
(function () {
  'use strict';

  // Guard: if this file is included twice, only the first copy runs.
  if (window.__kesslersDLLoaded) return;
  window.__kesslersDLLoaded = true;

  window.kesslersDL = Object.assign({ country: 'ZZ' }, window.kesslersDL || {});
  window.dataLayer = window.dataLayer || [];

  var DEBUG = /[?&]kdl_debug=1/.test(location.search);
  function log() { if (DEBUG && window.console) console.log.apply(console, ['[kesslersDL]'].concat([].slice.call(arguments))); }

  var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  // Drop an identical event if it was already pushed in the last `ms` milliseconds.
  var recent = {};
  function isDuplicate(key, ms) {
    var now = Date.now();
    if (recent[key] && now - recent[key] < ms) return true;
    recent[key] = now;
    return false;
  }

  // event_source lets GTM triggers tell our pushes apart from other tools'
  // (e.g. Shopify's Google & YouTube app gtag events, which use view_item/add_to_cart).
  // Sept 30, 2026: Evolear asked for unique names — kesslers_add_to_cart / kesslers_view_item —
  // because GTM's data model carried event_source into the next (Google app) event.
  function push(obj, hasEcommerce) {
    obj.event_source = 'kesslers_theme';
    if (hasEcommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(obj);
    log('push', obj);
  }

  /* ---------------- add_to_cart ---------------- */

  function isCartAdd(url) {
    try {
      var path = new URL(url, location.origin).pathname;
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) { return false; }
  }

  function toItem(line) {
    var price = (line.final_price != null ? line.final_price : line.price) / 100;
    return {
      item_id: 'shopify_' + window.kesslersDL.country + '_' + line.product_id + '_' + line.variant_id,
      product_id: String(line.product_id),
      variant_id: String(line.variant_id),
      item_name: line.product_title || line.title,
      item_variant: line.variant_title || '',
      sku: line.sku || '',
      item_brand: line.vendor || '',
      item_category: line.product_type || '',
      price: price,
      quantity: line.quantity || 1
    };
  }

  function pushAddToCart(json) {
    var lines = Array.isArray(json.items) ? json.items : (json.variant_id ? [json] : []);
    if (!lines.length) return;
    var items = lines.map(toItem);
    var value = items.reduce(function (sum, i) { return sum + i.price * i.quantity; }, 0);

    var key = 'atc:' + items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).join(',');
    if (isDuplicate(key, 2000)) { log('duplicate add_to_cart dropped', key); return; }

    push({
      event: 'kesslers_add_to_cart',
      ecommerce: { currency: currency, value: Math.round(value * 100) / 100, items: items }
    }, true);
  }

  function handleResponseText(text) {
    try {
      var json = JSON.parse(text);
      if (json && !json.status) pushAddToCart(json); // error responses have a status field
    } catch (e) {}
  }

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (isCartAdd(url)) {
        p.then(function (res) { if (res.ok) res.clone().text().then(handleResponseText); }).catch(function () {});
      }
      return p;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartAdd(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) handleResponseText(this.responseText);
      });
    }
    return origOpen.apply(this, arguments);
  };

  /* ---------------- submit_appointment ---------------- */
  // Hulk Form Builder posts {type:'form_submitted', payload} via postMessage after a
  // successful submit. Only the "Request Appointment" form counts, not Contact Us.
  // Contact-for-price product pages have BOTH forms (#contact-us and #make-appointment
  // modals), so we track which Hulk form the visitor last interacted with.

  var APPOINTMENT_FORM_ID = 'xDoYLtU1QEDIqOAEFlqHQQ';
  var CONTACT_FORM_ID = 'hbfq-1kVLdVNZMbpRyxfVw';
  var lastFormId = null;

  ['focusin', 'click', 'submit'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var el = e.target && e.target.closest && e.target.closest('.pxFormGenerator');
      if (el && el.id) lastFormId = el.id;
    }, true);
  });

  function isAppointmentForm(payload) {
    var s = '';
    try { s = typeof payload === 'string' ? payload : JSON.stringify(payload || {}); } catch (e) {}
    // 1. Hulk's payload names the form
    if (s.indexOf(APPOINTMENT_FORM_ID) > -1) return true;
    if (s.indexOf(CONTACT_FORM_ID) > -1) return false;
    // 2. The form the visitor was just filling in
    if (lastFormId) return lastFormId === APPOINTMENT_FORM_ID;
    // 3. The appointment form is the only Hulk form on the page
    var forms = document.querySelectorAll('.pxFormGenerator');
    return forms.length === 1 && forms[0].id === APPOINTMENT_FORM_ID;
  }

  window.addEventListener('message', function (e) {
    var data = e.data;
    if (typeof data === 'string') { try { data = JSON.parse(data); } catch (err) { return; } }
    if (!data || typeof data !== 'object' || !data.type) return;
    if (/form_/.test(data.type)) log('hulk message', data);
    if (data.type !== 'form_submitted') return;
    if (!isAppointmentForm(data.payload)) return;
    if (isDuplicate('appt', 5000)) return;

    push({
      event: 'submit_appointment',
      form_name: 'Request Appointment',
      form_id: APPOINTMENT_FORM_ID,
      form_location: /\/products\//.test(location.pathname) ? 'product_page_modal' : 'appointment_page',
      page_path: location.pathname
    }, false);
  });
})();
